import React, { useEffect, useState } from 'react'
import { EPersonalDetailsFormSteps } from '../../../../../../../../../types/enums/EPersonalDetailsFormSteps'
import { PersonalDetailsWrapper } from './style'
import { PhoneInput } from '../../../../../../../../../shared/components/phone-input/PhoneInput'
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../../../../../store/store'
import { IUser } from '../../../../../../../../../types/interfaces/IUser'
import { updateUser } from '../../../../../../../../../store/reducers/userReducer'
import { DatePickerWrapper } from '../../../../../../../../../shared/components/date-picker/DatePickerWrapper'
import { TextInput } from 'shared/components/text-input/TextInput'
import { GenderInput } from 'shared/components/gender-input/GenderInput'
import { useGenericFormState } from 'shared/hooks/useGenericState'
import { setFormTranslatedText } from 'pages/auth/utils/setFormTranslatedText'
import { useTranslation } from 'react-i18next'
import { setForm } from 'store/reducers/layoutReducer'
import { Button } from 'shared/components/button/Button'
import { PersonalInfoFormData } from 'types/types/PersonalInfoFormData'

type PersonalInfoFormErrors = Partial<
  Record<keyof PersonalInfoFormData, boolean>
>

export const PersonalDetailsFormPersonalInfo: React.FC = () => {
  const dispatch = useAppDispatch()
  const user = useAppSelector<IUser>((state) => state.user)
  const [personalInfoFormData, setPersonalInfoFormData] =
    useGenericFormState<PersonalInfoFormData>({
      firstName: user?.personalDetails?.firstName ?? '',
      lastName: user?.personalDetails?.lastName ?? '',
      dob: user?.personalDetails?.dob,
      phoneNum: user?.personalDetails?.phoneNum ?? '',
      gender: user?.personalDetails?.gender ?? undefined,
    })

  const [errors, setErrors] = useState<PersonalInfoFormErrors>({
    firstName: false,
    lastName: false,
    dob: false,
    phoneNum: false,
    gender: false,
  })
  const [isDisabled, setIsDisabled] = useState<boolean>(true)

  const { t } = useTranslation('', {
    keyPrefix: `pages.signUp.personalDetails`,
  })
  setFormTranslatedText('signUp.personalDetails')

  useEffect(() => {
    const isFormDataComplete = Object.values(personalInfoFormData).every(
      (value) => !!value,
    )
    const isValid = Object.values(errors).every((error) => !error)
    if (isFormDataComplete && isValid) {
      setIsDisabled(false)
    } else if (!isDisabled) {
      setIsDisabled(true)
    }
  }, [Object.values(personalInfoFormData), errors])

  const handleFieldChange = <T extends keyof typeof personalInfoFormData>(
    field: T,
    value?: (typeof personalInfoFormData)[T],
    error?: boolean,
  ) => {
    setErrors({ ...errors, [field]: error })
    setPersonalInfoFormData({ ...personalInfoFormData, [field]: value })
  }

  const nextButtonClicked = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault()
    dispatch(updateUser({ personalDetails: personalInfoFormData }))
    dispatch(
      setForm({
        personalDetailsStep: EPersonalDetailsFormSteps.identityProvider,
      }),
    )
  }

  return (
    <>
      <PersonalDetailsWrapper>
        <TextInput
          value={personalInfoFormData.firstName}
          minLength={2}
          hasError={errors.firstName}
          id={'firstName'}
          label={t('inputs.firstName.labelText')}
          errorText={t('inputs.firstName.errorText')}
          callback={(value, error) =>
            handleFieldChange('firstName', value, error)
          }
        />
        <TextInput
          value={
            personalInfoFormData.lastName !== 'temp'
              ? personalInfoFormData.lastName
              : ''
          }
          minLength={2}
          hasError={errors.lastName}
          id={'lastName'}
          label={t('inputs.lastName.labelText')}
          errorText={t('inputs.lastName.errorText')}
          callback={(value, error) =>
            handleFieldChange('lastName', value, error)
          }
        />
        <DatePickerWrapper
          value={personalInfoFormData.dob}
          hasError={errors.dob}
          labelText={t('inputs.dob.labelText')}
          errorText={t('inputs.dob.errorText')}
          callback={(value, error) => handleFieldChange('dob', value, error)}
        />
        <PhoneInput
          value={personalInfoFormData.phoneNum}
          hasError={errors.phoneNum}
          labelText={t('inputs.phoneNum.labelText')}
          errorText={t('inputs.phoneNum.errorText')}
          callback={(value, error) =>
            handleFieldChange('phoneNum', value, error)
          }
        />
      </PersonalDetailsWrapper>
      <GenderInput
        value={personalInfoFormData.gender}
        callback={(value, error) => handleFieldChange('gender', value, error)}
      ></GenderInput>
      <Button
        type="submit"
        disabled={isDisabled}
        className={`button ${isDisabled ? 'disabled' : ''}`}
        text={t('buttons.next.text')}
        callback={async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
          await nextButtonClicked(e)
        }
      />
    </>
  )
}
